import { forwardRef } from 'react'
import { cn } from '@shared/lib/utils'
import { Card, CardContent } from '@shared/components/ui/card'
import { Carousel } from '@shared/components/ui/carousel'

export interface FilterItem {
  value: string
  label: string
  disabled?: boolean
}

export interface StatusFilterCardsProps {
  filters: readonly FilterItem[]
  selectedStatus: string
  onStatusChange: (status: string) => void
  getStatusCount: (status: string) => number
  className?: string
  forceCarousel?: boolean
  variant?: 'count' | 'label'
}

const CAROUSEL_CONFIG = {
  threshold: 5,
  cardWidth: '140px',
  gap: '0.5rem'
} as const

const VARIANT_STYLES = {
  count: {
    card: 'cursor-pointer transition-all rounded-xl w-[140px] shrink-0 shadow-none',
    cardActive: 'bg-primary-light border-primary-light',
    cardInactive: 'bg-component border-transparent hover:border-zinc-300',
    container: 'p-4 flex flex-col items-start gap-1',
    count: 'text-xl font-mono font-bold leading-tight',
    countActive: 'text-primary',
    countInactive: 'text-foreground',
    label: 'text-xs font-mono uppercase tracking-wide leading-tight whitespace-nowrap',
    labelActive: 'text-primary',
    labelInactive: 'text-muted-foreground'
  },
  label: {
    card: 'cursor-pointer transition-all rounded-lg flex-1 min-w-fit shadow-none border',
    cardActive: 'bg-background border-[1.5px] border-primary',
    cardInactive: 'bg-background border-zinc-300 hover:border-primary',
    container: 'px-5 py-3',
    label: 'text-xs font-mono uppercase tracking-wide leading-tight whitespace-nowrap',
    labelActive: 'text-primary',
    labelInactive: 'text-muted-foreground'
  }
} as const

export const StatusFilterCards = forwardRef<HTMLDivElement, StatusFilterCardsProps>(
  ({ filters, selectedStatus, onStatusChange, getStatusCount, className, forceCarousel = false, variant = 'count' }, ref) => {
    
    // Label variant never uses carousel to allow cards to expand
    const useCarousel = variant === 'label' ? false : (filters.length > CAROUSEL_CONFIG.threshold || forceCarousel)
    const styles = VARIANT_STYLES[variant]

    const handleCardClick = (filterValue: string, isDisabled?: boolean) => {
      if (!isDisabled && onStatusChange) {
        onStatusChange(filterValue)
      }
    }

    const renderCard = (item: FilterItem) => {
      const isActive = selectedStatus === item.value
      
      return (
        <Card 
          key={item.value}
          className={cn(
            styles.card,
            isActive ? styles.cardActive : styles.cardInactive,
            item.disabled && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => handleCardClick(item.value, item.disabled)}
        >
          <CardContent className={styles.container}>
            {variant === 'count' && (
              <span className={cn(
                (styles as typeof VARIANT_STYLES.count).count,
                isActive ? (styles as typeof VARIANT_STYLES.count).countActive : (styles as typeof VARIANT_STYLES.count).countInactive
              )}>
                {getStatusCount(item.value) || '-'}
              </span>
            )}
            <p className={cn(
              styles.label,
              isActive ? styles.labelActive : styles.labelInactive
            )}>
              {item.label}
            </p>
          </CardContent>
        </Card>
      )
    }

    if (useCarousel) {
      return (
        <Carousel
          ref={ref}
          className={cn("w-full", className)}
          itemWidth={CAROUSEL_CONFIG.cardWidth}
          gap={CAROUSEL_CONFIG.gap}
          showNavigation
        >
          {filters.map(renderCard)}
        </Carousel>
      )
    }

    return (
      <div
        ref={ref}
        className={cn(
          'w-full flex gap-2',
          variant === 'label' ? 'overflow-x-auto' : 'overflow-x-auto px-1',
          className
        )}
      >
        {filters.map(renderCard)}
      </div>
    )
  }
)

StatusFilterCards.displayName = 'StatusFilterCards'